import React, { useContext, useState } from "react";
import { LanguageContext } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { MapPin, Phone, Mail, Clock } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const { language } = useContext(LanguageContext);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  const content = {
    en: {
      title: "Contact Us",
      subtitle: "Get in touch with us for any inquiries or assistance",
      info: "Contact Information",
      address: "Address",
      addressDetail:
        "Sri Siddhachal Sthulabhadra Jain Teerth Dham, Chandra Hills, Karnataka, India",
      phone: "Phone",
      phoneDetail: "+91 98868 76335",
      email: "Email",
      emailDetail: "info@srisiddhachal.org",
      hours: "Opening Hours",
      hoursDetail: "Daily: 6:00 AM - 8:00 PM",
      formTitle: "Send us a Message",
      name: "Full Name",
      namePlaceholder: "Enter your name",
      emailLabel: "Email Address",
      emailPlaceholder: "Enter your email",
      phoneLabel: "Phone Number",
      phonePlaceholder: "Enter your phone number",
      message: "Message",
      messagePlaceholder: "Your message...",
      submit: "Send Message",
      sending: "Sending...",
    },
    hi: {
      title: "संपर्क करें",
      subtitle: "किसी भी पूछताछ या सहायता के लिए हमसे संपर्क करें",
      info: "संपर्क जानकारी",
      address: "पता",
      addressDetail:
        "श्री सिद्धाचल स्थूलभद्र जैन तीर्थ धाम, चंद्र हिल्स, कर्नाटक, भारत",
      phone: "फ़ोन",
      phoneDetail: "+91 98868 76335",
      email: "ईमेल",
      emailDetail: "info@srisiddhachal.org",
      hours: "खुलने का समय",
      hoursDetail: "दैनिक: सुबह 6:00 - शाम 8:00",
      formTitle: "हमें एक संदेश भेजें",
      name: "पूरा नाम",
      namePlaceholder: "अपना नाम दर्ज करें",
      emailLabel: "ईमेल पता",
      emailPlaceholder: "अपना ईमेल दर्ज करें",
      phoneLabel: "फ़ोन नंबर",
      phonePlaceholder: "अपना फ़ोन नंबर दर्ज करें",
      message: "संदेश",
      messagePlaceholder: "आपका संदेश...",
      submit: "संदेश भेजें",
      sending: "भेजा जा रहा है...",
    },
  };

  const t = content[language];

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.name || !formData.email || !formData.message) {
      toast.error(
        language === "en"
          ? "Please fill all required fields"
          : "कृपया सभी आवश्यक फ़ील्ड भरें"
      );
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API}/contact`, formData);
      toast.success(
        language === "en"
          ? "Message sent successfully! We will get back to you soon."
          : "संदेश सफलतापूर्वक भेजा गया! हम जल्द ही आपसे संपर्क करेंगे।"
      );
      setFormData({ name: "", email: "", phone: "", message: "" });
    } catch (error) {
      console.error("Error sending message:", error);
      toast.error(
        language === "en"
          ? "Failed to send message. Please try again."
          : "संदेश भेजने में विफल। कृपया पुनः प्रयास करें।"
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="hero-section py-20 px-4" data-testid="contact-hero">
        <div className="container mx-auto max-w-5xl text-center">
          <h1
            className="text-4xl sm:text-5xl lg:text-6xl font-bold gradient-text mb-6"
            data-testid="contact-title"
          >
            {t.title}
          </h1>
          <p
            className="text-base md:text-lg text-amber-800"
            data-testid="contact-subtitle"
          >
            {t.subtitle}
          </p>
        </div>
      </section>

      {/* Content */}
      <section className="py-16 px-4 bg-white">
        <div className="container mx-auto max-w-6xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Information */}
            <div>
              <h2
                className="text-3xl font-bold text-amber-900 mb-8"
                data-testid="contact-info-title"
              >
                {t.info}
              </h2>
              <div className="space-y-6">
                <div
                  className="flex items-start space-x-4"
                  data-testid="contact-address"
                >
                  <MapPin className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-1">
                      {t.address}
                    </h3>
                    <p className="text-sm md:text-base text-amber-800">
                      {t.addressDetail}
                    </p>
                  </div>
                </div>

                <div
                  className="flex items-start space-x-4"
                  data-testid="contact-phone"
                >
                  <Phone className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-1">
                      {t.phone}
                    </h3>
                    <p className="text-sm md:text-base text-amber-800">
                      {t.phoneDetail}
                    </p>
                  </div>
                </div>

                <div
                  className="flex items-start space-x-4"
                  data-testid="contact-email"
                >
                  <Mail className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-1">
                      {t.email}
                    </h3>
                    <p className="text-sm md:text-base text-amber-800">
                      {t.emailDetail}
                    </p>
                  </div>
                </div>

                <div
                  className="flex items-start space-x-4"
                  data-testid="contact-hours"
                >
                  <Clock className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="font-semibold text-amber-900 mb-1">
                      {t.hours}
                    </h3>
                    <p className="text-sm md:text-base text-amber-800">
                      {t.hoursDetail}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="glass-effect p-8 rounded-2xl">
              <h2
                className="text-3xl font-bold text-amber-900 mb-6"
                data-testid="contact-form-title"
              >
                {t.formTitle}
              </h2>
              <form
                onSubmit={handleSubmit}
                className="space-y-4"
                data-testid="contact-form"
              >
                <div>
                  <Label htmlFor="name">{t.name} *</Label>
                  <Input
                    id="name"
                    data-testid="contact-name-input"
                    type="text"
                    placeholder={t.namePlaceholder}
                    value={formData.name}
                    onChange={(e) =>
                      setFormData({ ...formData, name: e.target.value })
                    }
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="email">{t.emailLabel} *</Label>
                  <Input
                    id="email"
                    data-testid="contact-email-input"
                    type="email"
                    placeholder={t.emailPlaceholder}
                    value={formData.email}
                    onChange={(e) =>
                      setFormData({ ...formData, email: e.target.value })
                    }
                    required
                  />
                </div>

                <div>
                  <Label htmlFor="phone">{t.phoneLabel}</Label>
                  <Input
                    id="phone"
                    data-testid="contact-phone-input"
                    type="tel"
                    placeholder={t.phonePlaceholder}
                    value={formData.phone}
                    onChange={(e) =>
                      setFormData({ ...formData, phone: e.target.value })
                    }
                  />
                </div>

                <div>
                  <Label htmlFor="message">{t.message} *</Label>
                  <Textarea
                    id="message"
                    data-testid="contact-message-input"
                    placeholder={t.messagePlaceholder}
                    value={formData.message}
                    onChange={(e) =>
                      setFormData({ ...formData, message: e.target.value })
                    }
                    rows={5}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  size="lg"
                  className="w-full btn-primary"
                  disabled={loading}
                  data-testid="contact-submit-btn"
                >
                  {loading ? t.sending : t.submit}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;
